// ---------------------------------------------------------------------------
// What the Sequence Progress panel says about one step
//
// REPORTED by NDI, 23 September 2026, against a live campaign: a lead who was
// already a connection showed BOTH steps ticked green and the panel read
// "Sequence Completed" — for a lead whose DM was deliberately never sent. In
// their words: "Already Connected ist nicht gleich Sequence Completed oder
// Done, sondern Sequence wird nicht gestartet."
//
// The cause is that the panel asked only one question:
//
//   const isDone = i < lead.currentStep;      // campaign-detail.ts
//
// `currentStep` counts how far the lead has MOVED, and a step moves the lead
// along whether it was sent or passed over. So every skipped step below the
// cursor rendered as a green tick — the strongest "we did this" the screen
// has — for work that was never done.
//
// A tick and a strike-through are not decoration. The operator reads them to
// decide whether to act, and a tick on a withheld DM says "nothing to do here"
// about the one lead that needs a decision.
//
// So the question is asked of the JOB, which knows what became of the step,
// and `currentStep` is used only where there is no job to ask — an email step
// that goes out through the provider leaves no job row, and its behaviour is
// deliberately unchanged.
//
// This is a transcription of the page's rule into somewhere it can be tested.
// The panel is a Lit template a unit test cannot reach, which is exactly why
// the rule went four hundred lines without one.
// ---------------------------------------------------------------------------

/** The states a step can be in, in the order the panel checks them. */
export type StepState = 'done' | 'skipped' | 'current' | 'upcoming';

/**
 * The part of an OutreachJob this decision needs. Deliberately not the whole
 * document: the panel must not start depending on fields it does not read.
 */
export interface TimelineJob {
  stepIndex: number;
  status: string;
  /**
   * Set by `skipThisJob`/`skipRemainingJobs` when the job was withheld pending
   * a PERSON's decision rather than finished with (server:
   * `helpers/lead-progress.ts`). It is what separates "we passed over this"
   * from "this is waiting for you".
   */
  awaitingDecision?: boolean;
}

/** A step that ended in one of these was not carried out. */
const NOT_CARRIED_OUT = new Set(['skipped', 'cancelled']);

/**
 * Was this step actually carried out?
 *
 * The job is the authority when there is one. Without a job — an email step
 * handled by the provider — fall back to the cursor, which is what the panel
 * has always done and is correct for a channel that reports no per-step state.
 */
export function stepWasCarriedOut(params: {
  stepIndex: number;
  currentStep: number;
  jobs: readonly TimelineJob[];
}): boolean {
  const forStep = params.jobs.filter(j => j.stepIndex === params.stepIndex);
  // No job to ask. An email step goes out through the provider and leaves no
  // row here, so the cursor is the only evidence there is — and it is the
  // right evidence for a channel that reports nothing per step.
  if (forStep.length === 0) return params.stepIndex < params.currentStep;
  // A job exists, so the job decides — in EVERY state, not only the skipped
  // ones. The first version of this function fell back to the cursor for a job
  // that was merely queued, which kept the green tick on a step whose own job
  // was still `pending` (found in production, 23 September 2026, on the lead
  // whose connect step was ticked while the button beneath offered to send it).
  //
  // More than one job can belong to a step — a failure followed by a retry —
  // and one that completed is enough.
  return forStep.some(j => j.status === 'completed');
}

/**
 * Is this step waiting on a PERSON rather than on the clock?
 *
 * A withheld DM and a step closed because the lead replied are both struck
 * through, but only the first is waiting for somebody — and only the first
 * should invite the operator to send it by hand.
 */
export function stepAwaitsDecision(params: { stepIndex: number; jobs: readonly TimelineJob[] }): boolean {
  // Only a step that was actually withheld waits for anybody. A completed job
  // carrying a stale flag is finished, and saying otherwise would put a
  // standing invitation to act on a lead that needs nothing.
  return params.jobs.some(
    j => j.stepIndex === params.stepIndex && NOT_CARRIED_OUT.has(j.status) && j.awaitingDecision === true,
  );
}

/**
 * Is this a LinkedIn step of a lead who ignored our invitation (WP47)?
 *
 * Then it is over, whatever its job says: a DM reaches only a connection, and
 * LinkedIn refuses a fresh invitation to them for weeks. The server already
 * closes these steps and offers none of them as the next step
 * (`helpers/next-step.ts`); the panel has to agree, or it keeps "Next" on a DM
 * the button beneath it will not send. Email steps are a different channel.
 */
export function linkedinStepEndedByIgnoredInvite(lead: { connectionState?: string }, channel: string): boolean {
  return lead.connectionState === 'invite_sent_ignored' && (channel === 'linkedin_connect' || channel === 'linkedin_dm');
}
